<script lang="ts">
  /**
   * フレーム列を 1 手ずつ再生する（components.md §3.1、viz-protocol.md §5.1）。
   * ← / → で 1 手、Home / End で先頭 / 進める範囲の末尾、Space で再生 / 停止。
   * pauseAt のステップより先へは進めない（予想を求める場面。親が答えたら外す）。
   */
  import type { Snippet } from 'svelte';
  import { onDestroy } from 'svelte';

  interface Props {
    frames: readonly unknown[];
    step?: number;
    autoplayMs?: number;
    pauseAt?: readonly number[];
    label?: string;
    /** フレームの描画。引数は現在のステップ（0 始まり） */
    children: Snippet<[number]>;
    onstep?: ((step: number) => void) | undefined;
  }

  let {
    frames,
    step = $bindable(0),
    autoplayMs = 600,
    pauseAt = [],
    label = 'ステップ操作',
    children,
    onstep,
  }: Props = $props();

  const last = $derived(Math.max(0, frames.length - 1));
  /** 進められる最大のステップ */
  const limit = $derived(Math.min(last, ...pauseAt.filter((p) => p >= 0)));
  const canBack = $derived(step > 0);
  const canForward = $derived(step < limit);

  function reducedMotion(): boolean {
    if (
      typeof document !== 'undefined' &&
      document.documentElement.dataset['motion'] === 'reduce'
    ) {
      return true;
    }
    return (
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
  const allowAutoplay = reducedMotion() === false;

  let playing = $state(false);
  let timer: ReturnType<typeof setInterval> | null = null;

  function go(n: number) {
    const next = Math.max(0, Math.min(n, limit));
    if (next === step) return;
    step = next;
    onstep?.(next);
  }

  function stop() {
    playing = false;
    if (timer !== null) clearInterval(timer);
    timer = null;
  }

  function play() {
    if (!allowAutoplay || !canForward) return;
    playing = true;
    timer = setInterval(() => {
      if (step >= limit) stop();
      else go(step + 1);
    }, autoplayMs);
  }

  function toggle() {
    if (playing) stop();
    else play();
  }

  $effect(() => {
    if (playing && step >= limit) stop();
  });
  onDestroy(stop);

  /** 外から呼ぶ: 1 手進める（カードのショートカット用） */
  export function forward(): boolean {
    if (!canForward) return false;
    go(step + 1);
    return true;
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
    let handled = true;
    if (e.key === 'ArrowLeft') go(step - 1);
    else if (e.key === 'ArrowRight') go(step + 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(limit);
    else if (e.key === ' ' && allowAutoplay && !(e.target instanceof HTMLButtonElement)) toggle();
    else handled = false;
    if (handled) e.preventDefault();
  }

  function onRange(e: Event) {
    const t = e.currentTarget;
    if (t instanceof HTMLInputElement) {
      go(Number(t.value));
      t.value = String(step);
    }
  }
</script>

<div class="step-player" data-testid="step-player">
  <div class="stage">{@render children(step)}</div>

  <div class="controls" role="toolbar" aria-label={label} tabindex="-1" {onkeydown}>
    <button class="btn btn-sm" type="button" disabled={!canBack} onclick={() => go(0)}>先頭</button>
    <button
      class="btn btn-sm"
      type="button"
      data-testid="step-back"
      disabled={!canBack}
      onclick={() => go(step - 1)}><span aria-hidden="true">←</span> 1 手戻す</button
    >
    {#if allowAutoplay}
      <button
        class="btn btn-sm"
        type="button"
        aria-pressed={playing}
        disabled={!playing && !canForward}
        onclick={toggle}>{playing ? '停止' : '再生'}</button
      >
    {/if}
    <button
      class="btn btn-sm btn-primary"
      type="button"
      data-testid="step-forward"
      disabled={!canForward}
      onclick={() => go(step + 1)}>1 手進む <span aria-hidden="true">→</span></button
    >
    <input
      class="range"
      type="range"
      min="0"
      max={last}
      value={step}
      aria-label="ステップ"
      aria-valuetext="ステップ {step + 1} / {frames.length}"
      oninput={onRange}
    />
    <span class="progress num" aria-live="polite" data-testid="step-progress"
      >ステップ {step + 1} / {frames.length}</span
    >
  </div>
</div>

<style>
  .step-player {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .controls {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .range {
    flex: 1;
    min-width: 8rem;
    accent-color: var(--accent);
  }
  .progress {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
</style>
